import { useEffect } from 'react';
import { apiClient } from '../api/api';
import { useUserStore } from '../store/useUserStore';
import { flagsEqual, flagsFor, userKey } from './flags';
import { planLoginMerge, serializeFlags, serverSupportsFlags, UI_FLAGS_FIELD } from './sync';
import { useOnboardingStore } from './useOnboardingStore';

const PUSH_DEBOUNCE_MS = 1500;

const pushFlags = (key: string) =>
  apiClient
    .patch('/custom_account/update-profile/', {
      [UI_FLAGS_FIELD]: serializeFlags(flagsFor(useOnboardingStore.getState().users, key)),
    })
    .catch(() => {
      // Flags still live in this browser; the next change or sign-in retries.
    });

/**
 * Keeps onboarding flags in step with the profile's `ui_flags`, so tours seen
 * on one device stay seen on others. Merges once per sign-in, then writes local
 * changes back (local wins after the merge, so "Reset tips" reaches the server).
 */
export function useOnboardingSync(): void {
  const userId = useUserStore((s) => s.user?.id);
  const supported = useUserStore((s) => serverSupportsFlags(s.user));

  useEffect(() => {
    if (!userId || !supported) return;
    const key = userKey(userId);
    const serverRaw = useUserStore.getState().user?.[UI_FLAGS_FIELD];
    const store = useOnboardingStore.getState();
    const { merged, push } = planLoginMerge(flagsFor(store.users, key), serverRaw);
    store.mergeFlags(key, merged);
    if (push) void pushFlags(key);

    let last = flagsFor(useOnboardingStore.getState().users, key);
    let timer: number | undefined;
    const unsubscribe = useOnboardingStore.subscribe((state) => {
      const next = flagsFor(state.users, key);
      if (flagsEqual(next, last)) return;
      last = next;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void pushFlags(key), PUSH_DEBOUNCE_MS);
    });
    return () => {
      unsubscribe();
      window.clearTimeout(timer);
    };
  }, [userId, supported]);
}
